import React, { useState, useEffect } from 'react';
import {
  useOrdenCompra,
  useActualizarOrdenCompra,
} from '../hooks/useOrdenesCompra';
import { useProveedores } from '../hooks/useProveedores';
import { useIngredientes } from '../hooks/useIngredientes';
import { fechaParaInput } from '../utils/dateUtils';
import {
  X,
  Save,
  Plus,
  Trash2,
  Package,
  User,
  Calendar,
  DollarSign,
} from 'lucide-react';

interface EditarOrdenCompraProps {
  isOpen: boolean;
  onClose: () => void;
  ordenId: string;
  onSuccess?: () => void;
}

interface FormItem {
  ingrediente_id: string;
  cantidad_solicitada: number;
  precio_unitario: number;
}

interface FormData {
  proveedor_id: number | '';
  fecha_entrega_esperada: string;
  observaciones: string;
  items: FormItem[];
}

const EditarOrdenCompra: React.FC<EditarOrdenCompraProps> = ({
  isOpen,
  onClose,
  ordenId,
  onSuccess,
}) => {
  const { data: orden, isLoading: loadingOrden } = useOrdenCompra(
    ordenId,
    isOpen
  );
  // limit altos: necesitamos que CUALQUIER proveedor o ingrediente que ya
  // tenga la orden esté presente como <option> en los selects, sino no
  // pueden pre-seleccionar el valor actual y el form arranca vacío visualmente.
  const { data: proveedoresData } = useProveedores({ limit: 500 });
  const { data: ingredientesData } = useIngredientes({ limit: 1000 });
  const actualizarMutation = useActualizarOrdenCompra(ordenId);

  const [formData, setFormData] = useState<FormData>({
    proveedor_id: '',
    fecha_entrega_esperada: '',
    observaciones: '',
    items: [],
  });

  const proveedores = proveedoresData?.data || [];
  const ingredientes = ingredientesData?.ingredientes || [];

  // Cargar datos de la orden cuando se abra el modal
  useEffect(() => {
    if (orden && isOpen) {
      // El adapter de ordenesCompraAPI expone `proveedor_id` (snake_case),
      // no `proveedorId` (camelCase). Usar la key real para que el select
      // pueda pre-seleccionar el proveedor actual.
      const provIdRaw = (orden as any).proveedor_id;
      setFormData({
        proveedor_id:
          provIdRaw != null && provIdRaw !== '' ? Number(provIdRaw) : '',
        fecha_entrega_esperada: orden.fechaEsperada
          ? fechaParaInput(orden.fechaEsperada)
          : '',
        observaciones: orden.observaciones || '',
        items:
          orden.items?.map((item) => ({
            ingrediente_id: item.ingrediente.id.toString(),
            cantidad_solicitada: item.cantidad,
            precio_unitario: item.precioUnitario,
          })) || [],
      });
    }
  }, [orden, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await actualizarMutation.mutateAsync(formData);

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error al actualizar orden:', error);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          ingrediente_id: '',
          cantidad_solicitada: 1,
          precio_unitario: 0,
        },
      ],
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_: FormItem, i: number) => i !== index),
    }));
  };

  const handleItemChange = (
    index: number,
    field: keyof FormItem,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item: FormItem, i: number) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const calcularSubtotal = (item: FormItem) => {
    return (item.cantidad_solicitada || 0) * (item.precio_unitario || 0);
  };

  const calcularTotal = () => {
    return formData.items.reduce(
      (total: number, item: FormItem) => total + calcularSubtotal(item),
      0
    );
  };

  if (!isOpen) return null;

  if (loadingOrden) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-white">
              Cargando orden...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!orden || orden.estado !== 'borrador') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No se puede editar
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Solo se pueden editar órdenes en estado borrador.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Editar Orden {orden.numero}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Proveedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                Proveedor
              </label>
              <select
                value={formData.proveedor_id}
                onChange={(e) =>
                  handleInputChange(
                    'proveedor_id',
                    e.target.value ? parseInt(e.target.value) : ''
                  )
                }
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Seleccionar proveedor</option>
                {proveedores.map((proveedor: any) => (
                  <option key={proveedor.id} value={proveedor.id}>
                    {proveedor.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha esperada */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="h-4 w-4 inline mr-1" />
                Fecha Esperada (Opcional)
              </label>
              <input
                type="date"
                value={formData.fecha_entrega_esperada}
                onChange={(e) =>
                  handleInputChange('fecha_entrega_esperada', e.target.value)
                }
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Observaciones */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observaciones
            </label>
            <textarea
              value={formData.observaciones}
              onChange={(e) =>
                handleInputChange('observaciones', e.target.value)
              }
              rows={3}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Observaciones adicionales..."
            />
          </div>

          {/* Items */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                <Package className="h-5 w-5 inline mr-2" />
                Items de la Orden
              </h3>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
              >
                <Plus className="h-4 w-4 mr-1" />
                Agregar Item
              </button>
            </div>

            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No hay items en la orden. Haz clic en "Agregar Item" para
                empezar.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.items.map((item: FormItem, index: number) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                      {/* Ingrediente */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Ingrediente
                        </label>
                        <select
                          value={item.ingrediente_id}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              'ingrediente_id',
                              e.target.value
                            )
                          }
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        >
                          <option value="">Seleccionar ingrediente</option>
                          {ingredientes.map((ingrediente: any) => (
                            <option
                              key={ingrediente.id}
                              value={ingrediente.id.toString()}
                            >
                              {ingrediente.nombre} (
                              {ingrediente.unidadMedida?.abreviacion ||
                                'unidad'}
                              )
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Cantidad */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Cantidad
                        </label>
                        <input
                          type="number"
                          value={item.cantidad_solicitada}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              'cantidad_solicitada',
                              Number(e.target.value)
                            )
                          }
                          min="0.01"
                          step="0.01"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        />
                      </div>

                      {/* Precio Unitario */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Precio Unit.
                        </label>
                        <input
                          type="number"
                          value={item.precio_unitario}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              'precio_unitario',
                              Number(e.target.value)
                            )
                          }
                          min="0"
                          step="0.01"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          required
                        />
                      </div>

                      {/* Subtotal y Eliminar */}
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Subtotal:
                          </span>
                          <div className="font-medium text-gray-900 dark:text-white">
                            ${calcularSubtotal(item).toLocaleString('es-CL')}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Eliminar item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-gray-900 dark:text-white">
                      <DollarSign className="h-5 w-5 inline mr-1" />
                      Total de la Orden:
                    </span>
                    <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      ${calcularTotal().toLocaleString('es-CL')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={
                actualizarMutation.isPending || formData.items.length === 0
              }
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actualizarMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarOrdenCompra;
